import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'; // Added useCallback
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/apiClient';
import { useQuery, useMutation, QueryClient, QueryClientProvider, useQueryClient as useTanstackQueryClient, focusManager } from '@tanstack/react-query';

import { toast } from "sonner";
import { Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Input, Skeleton, Accordion, AccordionContent, AccordionItem, AccordionTrigger, Tabs, TabsContent, TabsList, TabsTrigger, Label, Separator } from "@pagespeedify/ui"; // ADD Check and Separator
import { ChevronDown, Loader2, RefreshCw, Check, ChevronUp } from 'lucide-react'; // Added ChevronUp
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import {
  ApiResponse,
  CreateSubscriptionData,
  UpdateSubscriptionData,
  Subscription,
  Plan,
  SubscriptionStatus, PaddleEventData
} from '../types';

import { TrialStatus } from '../components/subscription/TrialStatus';
import { PlanChangeModal } from '../components/subscription/PlanChangeModal';
import { InvoiceHistory } from '../components/subscription/InvoiceHistory';

// Using global Window interface with Paddle from types/index.ts

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes - keep data fresh longer
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
    mutations: {
      retry: 0,
    }
  },
});

// --- START: Define/Update these constants ---
const LIVE_FEATURE_KEYS: string[] = [
  'reports',
  'agent_compute_units',
  'image_optimizations',
  'monitoring_metrics',
  'monitoring_websites',
];
const planFeatureDisplayLabels: Record<string, string> = {
  reports: 'PageSpeed Reports',
  agent_compute_units: 'Compute Units',
  image_optimizations: 'Image Optimizations',
  monitoring_metrics: 'metrics monitored',
  monitoring_websites: 'websites monitored',
};

const PlanFeaturesDisplay: React.FC<{
  features: any;
  showActivationPerks?: boolean;
  className?: string;
  showMonthlyEquivalent?: boolean;
  billingTerm?: 'monthly' | 'yearly' | string; // Added billingTerm prop
  highlightAsNewUserBonus?: boolean; // ADD THIS PROP

}> = ({ features, showActivationPerks = true, className = "", showMonthlyEquivalent = false, billingTerm: propBillingTerm, highlightAsNewUserBonus = false
}) => {
    if (!features || typeof features !== 'object') return null;

    const termAllocationsData = features?.term_included_allocations;
    const activationPerks = features.activation_perks;
    const overageCostsCredits = features?.overage_costs_credits; // Get overage costs
    const effectiveBillingTerm = propBillingTerm || features.billing_term;
    const isEnterprisePlan = features?.plan_user_type_granted === 'enterprise';

    const getBillingPeriodText = () => {
      if (effectiveBillingTerm === 'monthly') return 'per month';
      if (effectiveBillingTerm === 'yearly') return 'per year';
      return '';
    };

    const liveTermAllocations = termAllocationsData && typeof termAllocationsData === 'object'
      ? Object.entries(termAllocationsData).filter(([key]) => LIVE_FEATURE_KEYS.includes(key as string))
      : [];

    const liveOverageCosts = overageCostsCredits && typeof overageCostsCredits === 'object'
      ? Object.entries(overageCostsCredits).filter(([key]) => LIVE_FEATURE_KEYS.includes(key as string))
      : [];

    return (
      <div className={`space-y-4 ${className}`}> {/* Increased default spacing slightly */}
        {/* Included Allocations */}
        {liveTermAllocations.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2">
              Included with this plan:
            </h4>
            <ul className="space-y-1.5 text-sm">
              {liveTermAllocations.map(([key, value]) => {
                const label = planFeatureDisplayLabels[key as string] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                const periodText = (key === 'monitoring_metrics' || key === 'monitoring_websites') ? '' : getBillingPeriodText();
                const monthlyEquiv = showMonthlyEquivalent && effectiveBillingTerm === 'yearly' && typeof value === 'number' && value > 0
                  ? ` (~${Math.round(Number(value) / 12)}/month)`
                  : '';

                return (
                  <li key={`included-${key}`} className="flex justify-left text-muted-foreground">
                    <Check className="w-4 h-4 mr-2 mt-[3px] text-green-600 dark:text-green-500 flex-shrink-0" />
                    <span>
                      <strong className="text-foreground">{String(value)}</strong> {label} {periodText}
                      {monthlyEquiv && <span className="text-xs text-muted-foreground ml-1">{monthlyEquiv}</span>}
                    </span>
                  </li>
                );
              })}
              <li className="flex justify-left text-muted-foreground"><Check className="w-4 h-4 mr-2 mt-[3px] text-green-600 dark:text-green-500 flex-shrink-0" />
                Github Integration</li>
              <li className="flex justify-left text-muted-foreground"><Check className="w-4 h-4 mr-2 mt-[3px] text-green-600 dark:text-green-500 flex-shrink-0" />
                Tailored insights</li>
              <li className="flex justify-left text-muted-foreground"><Check className="w-4 h-4 mr-2 mt-[3px] text-green-600 dark:text-green-500 flex-shrink-0" />
                Task Management</li>
            </ul>
          </div>
        )}

        {/* Activation Perks */}
        {showActivationPerks && activationPerks?.credits && Number(activationPerks.credits) > 0 && (
          <div className="mt-3"> {/* Adjusted margin if needed */}
            <div className="flex items-center gap-2 p-2.5 bg-primary/10 dark:bg-primary/20 rounded-md">
              <span className="text-xl">🎁</span>
              <span className="text-sm font-medium text-foreground">
                <strong>{activationPerks.credits} free credits</strong> on first activation
              </span>
            </div>
          </div>
        )}

        {/* Trial messaging */}
        {showActivationPerks && (
          <p className="mt-2 text-xs text-center text-muted-foreground">
            ✨ 7-day free trial • Cancel anytime
          </p>
        )}

        {/* Unlimited Access With Credits */}
        {liveOverageCosts.length > 0 && (
          <div className="pt-3 mt-3 border-t border-border"> {/* Added border-t for separation */}
            <h4 className="text-sm font-semibold text-foreground mb-2">
              Unlimited Access With Credits:
            </h4>
            <ul className="space-y-1.5 text-sm">
              {liveOverageCosts.map(([key, value]) => {
                if (isEnterprisePlan && key === 'monitoring_metrics') {
                  return null; // Don't render this item for enterprise plans
                }
                return (
                  <li key={`overage-${key}`} className="flex justify-left text-muted-foreground">
                    <Check className="w-4 h-4 mr-2 mt-[3px] text-green-600 dark:text-green-500 flex-shrink-0" />
                    <span>
                      {key === 'monitoring_metrics' ? (
                        <><strong className="text-foreground">{String(value)} credits</strong> per extra metric</>
                      ) : key === 'monitoring_websites' ? (
                        <><strong className="text-foreground">{String(value)} credits</strong> per extra website</>
                      ) : (
                        <>{(planFeatureDisplayLabels[key as string] || key)}: <strong className="text-foreground">{String(value)} credits</strong> per unit</>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  };
// --- END: Updated PlanFeaturesDisplay Component ---

// Add persistence layer (if in browser environment)
if (typeof window !== 'undefined') {
  const persister = createSyncStoragePersister({
    storage: window.sessionStorage,
  });

  persistQueryClient({
    queryClient,
    persister,
    maxAge: 1000 * 60 * 60, // 1 hour
  });
}

focusManager.setEventListener((notifyReactQueryOfFocus) => {
  let customEventForRefetch: Event | undefined;

  // Define our own handlers that will call notifyReactQueryOfFocus
  const onAppFocus = () => {
    notifyReactQueryOfFocus(true);
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      notifyReactQueryOfFocus(true);
    }
  };

  const onSubscriptionOperationCompleted = () => {
    console.log('[SP_LOG] subscription-operation-completed event received by focusManager listener');
    notifyReactQueryOfFocus(true); // Tell React Query to check for updates
  };

  if (typeof window !== 'undefined' && typeof document !== 'undefined' && window.addEventListener) {
    customEventForRefetch = new Event('subscription-operation-completed');

    // Listen to standard browser focus events
    window.addEventListener('focus', onAppFocus, false);
    window.addEventListener('visibilitychange', onVisibilityChange, false);

    // Listen to our custom event
    window.addEventListener('subscription-operation-completed', onSubscriptionOperationCompleted, false);

    // Setup the global dispatcher
    window.dispatchSubscriptionOperationCompleted = () => {
      if (customEventForRefetch) {
        console.log('[SP_LOG] Dispatching subscription-operation-completed event via window.dispatch');
        window.dispatchEvent(customEventForRefetch);
      }
    };

    // Return the cleanup function
    return () => {
      window.removeEventListener('focus', onAppFocus);
      window.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('subscription-operation-completed', onSubscriptionOperationCompleted);
      delete window.dispatchSubscriptionOperationCompleted;
    };
  }

  // Fallback cleanup if addEventListener was not called
  return () => { };
});
// Using PaddleEventData interface from types/index.ts

const getErrorMessage = (error: any, defaultMessage: string): string => {
  if (!error) return defaultMessage;
  if (error.response && error.response.data) {
    const data = error.response.data;
    if (typeof data.message === 'string' && data.message.trim() !== '') return data.message;
    if (typeof data.error === 'string' && data.error.trim() !== '') return data.error;
    if (typeof data === 'string' && data.trim() !== '') return data;
  }
  if (error.message && typeof error.message === 'string' && error.message.trim() !== '') {
    return error.message;
  }
  if (typeof error === 'string' && error.trim() !== '') return error;
  return defaultMessage;
};

const fetchUserSubscriptions = async (api: ReturnType<typeof useApi>): Promise<Subscription[]> => {
  console.log('[SP_LOG] fetchUserSubscriptions - Called');
  const response = await api.get<ApiResponse<Subscription[]>>(`/subscriptions`);
  console.log('[SP_LOG] fetchUserSubscriptions - API Response:', JSON.stringify(response, null, 2));
  if (!response || typeof response.success !== 'boolean') {
    console.error('[SP_LOG] fetchUserSubscriptions - Invalid API response structure:', response);
    throw new Error('Invalid API response structure when fetching subscriptions.');
  }
  if (!response.success) {
    console.error('[SP_LOG] fetchUserSubscriptions - API call not successful:', response.message);
    throw new Error(response.message || `Failed to fetch subscriptions (API error)`);
  }
  if (!Array.isArray(response.data)) {
    console.error('[SP_LOG] fetchUserSubscriptions - response.data is not an array:', response.data);
    throw new Error(response.message || 'Subscription data is missing or not in expected format.');
  }
  console.log('[SP_LOG] fetchUserSubscriptions - Returning response.data. Length:', response.data.length);
  return response.data;
};

const fetchSpecificSubscriptionById = async (api: ReturnType<typeof useApi>, subscriptionId: string): Promise<Subscription> => {
  console.log(`[SP_LOG] fetchSpecificSubscriptionById - Called with ID: ${subscriptionId}`);
  const response = await api.get<ApiResponse<Subscription>>(`/subscriptions/${subscriptionId}`);
  console.log(`[SP_LOG] fetchSpecificSubscriptionById - API Response for ${subscriptionId}:`, JSON.stringify(response, null, 2));
  if (!response || typeof response.success !== 'boolean') {
    console.error(`[SP_LOG] fetchSpecificSubscriptionById - Invalid API response structure for ${subscriptionId}:`, response);
    throw new Error('Invalid API response structure when fetching specific subscription.');
  }
  if (!response.success || !response.data) {
    console.error(`[SP_LOG] fetchSpecificSubscriptionById - API call not successful or data missing for ${subscriptionId}:`, response.message);
    throw new Error(response.message || `Failed to fetch specific subscription ${subscriptionId} (API error)`);
  }
  console.log(`[SP_LOG] fetchSpecificSubscriptionById - Returning response.data for ${subscriptionId}`);
  return response.data;
};

const fetchSubscriptionPlans = async (api: ReturnType<typeof useApi>): Promise<Plan[]> => {
  const response = await api.get<ApiResponse<Plan[]>>('/subscriptions/plans');
  if (!response || typeof response.success !== 'boolean') {
    console.error('Invalid API response structure for plans:', response);
    throw new Error('Invalid API response structure when fetching plans.');
  }
  if (!response.success) {
    throw new Error(response.message || `Failed to fetch plans (API error)`);
  }
  if (!Array.isArray(response.data)) {
    console.error('Plan data is not an array despite success:', response);
    throw new Error(response.message || 'Plan data is missing or not in expected format.');
  }
  return response.data;
};

interface PersistentMessage {
  type: 'success' | 'error' | 'info' | 'warning'; // Added 'warning'
  text: string;
}

// Add a debug flag at the top of your component:
const DEBUG = true; // Temporarily set to true for debugging, then set back to false

export default function SubscriptionsPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <SubscriptionsContent />
    </QueryClientProvider>
  );
}

import { useLocation, useNavigate } from 'react-router-dom';

function SubscriptionsContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const planToSubscribe = searchParams.get('plan');
  const redirectAfterSubscribe = searchParams.get('redirect_after_subscribe');

  // Add it here with your other state declarations
  const [lastProcessedCheckoutId, setLastProcessedCheckoutId] = useState<string | null>(null);
  // Enhanced processing states for granular feedback
  const [processingStates, setProcessingStates] = useState({
    creating: false,
    changing: false,
    canceling: false,
    reactivating: false,
  });

  // Legacy processing state for backward compatibility
  const isProcessing = Object.values(processingStates).some(state => state);

  const [persistentMessage, setPersistentMessage] = useState<PersistentMessage | null>(null);
  const [promoCode] = useState('');
  const [isPlanChangeModalOpen, setIsPlanChangeModalOpen] = useState(false);

  // ADD THIS STATE VARIABLE:
  const [pendingCheckoutAction, setPendingCheckoutAction] = useState<{
    actionType: 'changePlan' | 'createSubscription' | null; // Added 'createSubscription'
    transactionId: string | null;
    subscriptionIdToUpdate?: string | null; // Optional: for changePlan
    newPlanName?: string; // For both, represents the target plan name
    newSubscriptionPaddleId?: string | null; // Optional: for createSubscription
  }>({
    actionType: null,
    transactionId: null,
    subscriptionIdToUpdate: null,
    newPlanName: undefined,
    newSubscriptionPaddleId: null
  });


  const [activeSubscriptionId, setActiveSubscriptionId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false); // Re-introduced for toggle button
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isRefreshingData, setIsRefreshingData] = useState(false);

  // New states for enhanced UI
  const [paddleStatus, setPaddleStatus] = useState<{
    isVisible: boolean;
    status: 'loading' | 'processing' | 'success' | 'error' | 'closed';
    message: string;
  }>({ isVisible: false, status: 'loading', message: '' });

  const [showSuccessBanner, setShowSuccessBanner] = useState<{
    isVisible: boolean;
    operation: 'create' | 'change' | 'cancel' | 'reactivate';
    planName?: string;
  }>({ isVisible: false, operation: 'create' });
  const api = useApi();
  const { user, refreshUser, isLoading: isAuthLoading } = useAuth();
  const tanstackQueryClient = useTanstackQueryClient();
  // Helper function to update processing states
  const updateProcessingState = useCallback((
    operation: keyof typeof processingStates,
    isProcessing: boolean
  ) => {
    setProcessingStates(prev => ({ ...prev, [operation]: isProcessing }));
  }, []);

  // Check if isProcessing exists - if it's not defined already, add this:

  // Define DEBUG or ensure it's sourced correctly (e.g., from environment variables)
  // const DEBUG = process.env.NODE_ENV === 'development'; // Example definition

  useEffect(() => {
    if (DEBUG && user) {
      // console.log('[SP_LOG] User object in SubscriptionsContent (from useAuth):', JSON.stringify(user, null, 2)); // Too verbose
      // console.log('[SP_LOG] User has_billing_history (from useAuth):', user.has_billing_history);
    } else if (!isAuthLoading && !user) { // Check for !user explicitly if DEBUG is false
      // console.log('[SP_LOG] User object in SubscriptionsContent is null (DEBUG is false or user is null) and not auth loading.');
    }
    // If DEBUG is a constant like above, it doesn't need to be in the dependency array
    // unless it can change during the component's lifecycle, which is unlikely for this kind of flag.
  }, [user, isAuthLoading]); // Removed DEBUG from deps if it's a module-level or function-level constant

  const {
    data: subscriptionDataArray,
    isLoading: isLoadingSubscriptionsList,
    error: subscriptionsListErrorObj,
    refetch: refetchSubscriptionsList
  } = useQuery<Subscription[], Error>({
    queryKey: ['userSubscriptionsList', user?.uid],
    queryFn: () => fetchUserSubscriptions(api),
    enabled: !!user?.uid && !isAuthLoading, // Also ensure auth isn't loading
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: false, // Don't auto-refetch periodically
  });
  const isEligibleForNewUserBonusMessage = !user?.granted_plan_tier_activation_perks || user.granted_plan_tier_activation_perks.length === 0;

  // Effect to explicitly refetch subscriptions when user logs in or user context changes
  const prevUserUidRef = useRef<string | undefined>();

  useEffect(() => {
    const currentUid = user?.uid;
    if (currentUid && !isAuthLoading) {
      // Refetch if:
      // 1. User UID has changed (e.g., different user logs in).
      // 2. User UID was previously undefined (user was logged out or auth was loading) and is now defined.
      if (prevUserUidRef.current !== currentUid) {
        // console.log(`[SP_LOG] User UID changed or became available (from ${prevUserUidRef.current} to ${currentUid}). Explicitly refetching subscriptions list.`);
        refetchSubscriptionsList();
      }
    }
    // Update prevUserUidRef for the next render AFTER the current effect logic.
    prevUserUidRef.current = currentUid;
  }, [user?.uid, isAuthLoading, refetchSubscriptionsList]); const hasFullDataInSubscriptionsArray = useCallback(() => {
    if (!subscriptionDataArray || !activeSubscriptionId) return false;
    const match = subscriptionDataArray.find(sub => sub.paddle_subscription_id === activeSubscriptionId);
    // Check if the subscription data has all the fields we need
    return !!match && !!match.current_period_end && typeof match.cancel_at_period_end !== 'undefined';
  }, [subscriptionDataArray, activeSubscriptionId]);
  const {
    data: detailedSubscription,
    isLoading: isLoadingDetailedSubscription,
    error: detailedSubscriptionErrorObj,
    isFetching: isFetchingDetailedSubscription
  } = useQuery<Subscription, Error>({
    queryKey: ['userSubscriptionDetail', user?.uid, activeSubscriptionId],
    queryFn: () => {
      if (!activeSubscriptionId) {
        // console.warn('[SP_LOG] userSubscriptionDetail - queryFn called but activeSubscriptionId is null.'); // Keep important warnings
        throw new Error("No active subscription ID to fetch details for.");
      }
      return fetchSpecificSubscriptionById(api, activeSubscriptionId);
    },
    enabled: !!user?.uid && !!activeSubscriptionId && !isAuthLoading && !hasFullDataInSubscriptionsArray(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: false,
  });
  const {
    data: plansArray, // This is your primary source for all 6 plan offerings
    isLoading: isLoadingPlans,
    error: plansErrorObj
  } = useQuery<Plan[], Error>(
    {
      queryKey: ['subscriptionPlans'],
      queryFn: () => fetchSubscriptionPlans(api),
      enabled: !!user?.uid && !isAuthLoading,
    }
  );

  // Handle plans query error with useEffect
  useEffect(() => {
    if (plansErrorObj) {
      const errorMessage = getErrorMessage(plansErrorObj, 'An unknown error occurred while loading subscription plans.');
      console.error('Failed to load subscription plans:', plansErrorObj);
      toast.error(errorMessage);
    }
  }, [plansErrorObj]);

  const currentSubscriptionToDisplay = useMemo(() => {
    return detailedSubscription && detailedSubscription.paddle_subscription_id === activeSubscriptionId
      ? detailedSubscription
      : (subscriptionDataArray && Array.isArray(subscriptionDataArray) && subscriptionDataArray.length > 0 && activeSubscriptionId && subscriptionDataArray.find(sub => sub.paddle_subscription_id === activeSubscriptionId)) // Ensure activeSubscriptionId is used to find the correct one
        ? subscriptionDataArray.find(sub => sub.paddle_subscription_id === activeSubscriptionId)
        : undefined;
  }, [detailedSubscription, activeSubscriptionId, subscriptionDataArray]);

  // == NEW LOGIC FOR PlanChangeModal PROPS - PLACE IT HERE ==
  const allPlansFromApi = useMemo(() => (Array.isArray(plansArray) ? plansArray : []), [plansArray]);

  // Updated getCurrentPlanDisplay function to include billing cycle
  const getCurrentPlanDisplay = useCallback(() => {
    if (!user?.user_type) return 'Unknown Plan';

    // Map user_type to plan names
    const planTypeMap: Record<string, string> = {
      'free': 'Free',
      'basic': 'Basic',
      'pro': 'Pro',
      'enterprise': 'Enterprise',
      'unsubscribed': 'Unsubscribed'
    };

    const userTypeLower = user.user_type.toLowerCase();
    const planName = planTypeMap[userTypeLower] || user.user_type;

    // Don't append billing cycle for unsubscribed or free users
    if (userTypeLower === 'unsubscribed' || userTypeLower === 'free') {
      return planName;
    }

    // Get billing cycle from subscription data
    let billingCycle = '';
    if (currentSubscriptionToDisplay) {
      const currentPlanObj = allPlansFromApi.find(p => p.id === currentSubscriptionToDisplay.plan_id);
      // ✅ IMPROVED: Better billing cycle detection
      if (currentPlanObj?.name?.includes('Yearly') ||
        (currentPlanObj?.price_yearly && currentPlanObj.price_yearly > 0 && !currentPlanObj?.price_monthly)) {
        billingCycle = 'Yearly';
      } else if (currentPlanObj?.name?.includes('Monthly') ||
        (currentPlanObj?.price_monthly && currentPlanObj.price_monthly > 0)) {
        billingCycle = 'Monthly';
      }
    }

    return billingCycle ? `${planName} ${billingCycle}` : planName;
  }, [user?.user_type, currentSubscriptionToDisplay, allPlansFromApi]);

  useEffect(() => {
    if (subscriptionsListErrorObj) {
      const errorMessage = getErrorMessage(subscriptionsListErrorObj, 'An error occurred while loading your subscriptions.');
      console.error('[SP_LOG] userSubscriptionsList - useEffect handling error:', subscriptionsListErrorObj); // Keep error logs
      toast.error(errorMessage);
      setPersistentMessage({ type: 'error', text: errorMessage });
    } else if (subscriptionDataArray) {
      // console.log('[SP_LOG] userSubscriptionsList - useEffect processing data. Count:', subscriptionDataArray.length);
      if (!detailedSubscriptionErrorObj && !subscriptionsListErrorObj) {
        setPersistentMessage(null);
      }

      if (subscriptionDataArray.length > 0) {
        // First priority: Active subscriptions that are NOT scheduled to cancel
        const trulyActiveSub = subscriptionDataArray.find(s =>
          (s.status === SubscriptionStatus.ACTIVE || s.status === SubscriptionStatus.TRIALING) &&
          !s.cancel_at_period_end
        );

        // Second priority: Any active subscription (even if scheduled to cancel)
        const anyActiveSub = subscriptionDataArray.find(s =>
          s.status === SubscriptionStatus.ACTIVE || s.status === SubscriptionStatus.TRIALING
        );

        // Fall back to the first subscription in the array if no active ones found
        const preferredSub = trulyActiveSub || anyActiveSub || subscriptionDataArray[0];

        if (preferredSub && typeof preferredSub.paddle_subscription_id === 'string') {
          if (activeSubscriptionId !== preferredSub.paddle_subscription_id) {
            setActiveSubscriptionId(preferredSub.paddle_subscription_id);
            // Log which subscription was selected and why
            // console.log(`[SP_LOG] Selected subscription ${preferredSub.paddle_subscription_id} as active. Status: ${preferredSub.status}, Canceling: ${!!preferredSub.cancel_at_period_end}, Plan ID: ${preferredSub.plan_id}`);
          }
        }
      } else {
        // console.log('[SP_LOG] userSubscriptionsList - useEffect. Data is empty array. Setting activeSubscriptionId to null.');
        if (activeSubscriptionId !== null) {
          setActiveSubscriptionId(null);
        }
      }
    }
  }, [
    subscriptionDataArray,
    subscriptionsListErrorObj,
    detailedSubscriptionErrorObj,
    activeSubscriptionId
  ]);
  const createSubscriptionMutation = useMutation<ApiResponse<CreateSubscriptionData>, Error, { planId: string; promoCode?: string }>({
    mutationFn: (data) => api.post<ApiResponse<CreateSubscriptionData>>('/subscriptions', data),
    onMutate: (variables) => {
      // console.log('[SP_LOG] Create Subscription: onMutate triggered.');

      // Get plan name for specific messaging
      const planDetails = allPlansFromApi.find(p => p.paddle_price_id === variables.planId);
      const planName = planDetails?.name || 'selected plan';
      setPersistentMessage(null); // Clear any previous persistent messages
      updateProcessingState('creating', true);
      setPaddleStatus({
        isVisible: true,
        status: 'processing',
        message: `Creating subscription to ${planName}...`
      });
    },
    onSuccess: (response, variables) => {
      const transactionId = response.data?.paddle_transaction_id || response.data?.transactionId;
      const newSubscriptionPaddleIdFromApi = response.data?.paddle_subscription_id || response.data?.subscriptionId;

      const planDetails = allPlansFromApi.find(p => p.paddle_price_id === variables.planId);
      const planName = planDetails?.name || 'selected plan';

      if (response.success && transactionId && window.Paddle && typeof window.Paddle.Checkout.open === 'function') {
        // --- SET PENDING CHECKOUT ACTION FOR CREATE SUBSCRIPTION ---
        // console.log(`[SP_LOG_CREATE_SUB_DEBUG] Attempting to set pendingCheckoutAction for Create Subscription.`);
        // console.log(`[SP_LOG_CREATE_SUB_DEBUG] Details: actionType='createSubscription', transactionId='${transactionId}', newSubscriptionPaddleId='${newSubscriptionPaddleIdFromApi}', planName='${planName}'`);
        setPendingCheckoutAction({
          actionType: 'createSubscription',
          transactionId: transactionId,
          newSubscriptionPaddleId: newSubscriptionPaddleIdFromApi,
          newPlanName: planName, // Changed from planName to newPlanName
          subscriptionIdToUpdate: null // Not used for create in the same way as change
        });
        // console.log(`[SP_LOG_CREATE_SUB_DEBUG] pendingCheckoutAction set for Create Subscription.`);
        // --- END OF SET PENDING CHECKOUT ACTION ---

        toast.info(`Redirecting to payment for ${planName}...`);
        setPaddleStatus({
          isVisible: true,
          status: 'processing',
          message: `Opening checkout for ${planName}...`
        });

        window.Paddle.Checkout.open({
          transactionId: transactionId,
          // --- START OF MODIFIED MINIMAL eventCallback for Create Subscription ---
          eventCallback: function (eventData: PaddleEventData) {
            // console.log('[[SP_LOG_CREATE_SUB]] MINIMAL LOCAL Create Sub eventCallback INVOKED. Event Name:', eventData.name, eventData); // Can be verbose
            const currentPlanNameForCallback = planName;

            if (eventData.name === 'checkout.loaded') {
              setPaddleStatus({ isVisible: true, status: 'processing', message: `Please complete payment for ${currentPlanNameForCallback}...` });
            } else if (eventData.name === 'checkout.closed') {
              // console.log('[[SP_LOG_CREATE_SUB]] Minimal Local callback: checkout.closed. Was payment completed?', eventData.data?.checkout?.status === 'completed');
              if (eventData.data?.checkout?.status !== 'completed') {
                toast.info(`${currentPlanNameForCallback} checkout was closed before completion.`);
                setPersistentMessage({ type: 'warning', text: `Subscription to ${currentPlanNameForCallback} was not completed.` });
                updateProcessingState('creating', false);
                setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
                // Clear pending action if user closes manually
                setPendingCheckoutAction({
                  actionType: null,
                  transactionId: null,
                  subscriptionIdToUpdate: null,
                  newPlanName: undefined,
                  newSubscriptionPaddleId: null
                });

                // Invalidate queries if checkout closed prematurely
                const idToInvalidate = newSubscriptionPaddleIdFromApi; // Use ID from API response
                if (user?.uid && idToInvalidate) {
                  tanstackQueryClient.invalidateQueries({ queryKey: ['userSubscriptionsList', user.uid] });
                  tanstackQueryClient.invalidateQueries({ queryKey: ['userSubscriptionDetail', user.uid, idToInvalidate] });
                }
                if (window.dispatchSubscriptionOperationCompleted) {
                  window.dispatchSubscriptionOperationCompleted();
                }
              }
            }
            // NO 'checkout.completed' success logic here for createSubscription;
            // It will be handled by the global custom event listener.
          }
          // --- END OF MODIFIED MINIMAL eventCallback ---
        });
      } else if (response.success && response.message && (response.message.includes("No payment required") || response.message.includes("successfully created"))) {
        const handleSuccessLocal = async () => {
          toast.success(`Successfully subscribed to ${planName}!`); // 1. Specific Toast
          // No setPersistentMessage for success
          // No setPaddleStatus to 'success'

          try {
            await handleMutationSuccess(newSubscriptionPaddleIdFromApi);

            setShowSuccessBanner({ // 2. Specific Banner
              isVisible: true,
              operation: 'create',
              planName: planName
            });

            // If redirect_after_subscribe is set, redirect after a short delay
            if (redirectAfterSubscribe) {
              toast.info('Redirecting you to your report...');
              setTimeout(() => {
                navigate(decodeURIComponent(redirectAfterSubscribe));
              }, 2000); // 2s delay to show success message
            } else {
              setTimeout(() => {
                setShowSuccessBanner({ isVisible: false, operation: 'create' });
              }, 8000); // Consistent 8s
            }

          } catch (e) {
            console.error('[SP_LOG] Create Subscription (no checkout): Error in handleMutationSuccess:', e);
            toast.error('Subscription created, but an error occurred updating the page. Please refresh.');
          }
          finally {
            updateProcessingState('creating', false);
            setPaddleStatus({ isVisible: false, status: 'loading', message: '' }); // Clear/hide PaddleStatusDisplay
          }
        };
        handleSuccessLocal();
      } else {
        // This is the existing error handling before checkout - KEEP AS IS
        const errorMsg = !response.success ? (response?.message || response?.error || `Failed to create subscription to ${planName}.`) :
          !transactionId ? `Could not initiate payment for ${planName}. Transaction details missing.` :
            !window.Paddle ? 'Payment provider (Paddle) is not available.' :
              'An unexpected error occurred before opening checkout.';
        toast.error(errorMsg);
        if (setPersistentMessage && !response.success) setPersistentMessage({ type: 'error', text: errorMsg });
        updateProcessingState('creating', false);
        setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
      }
    },
    onError: (error: any, variables) => {
      console.error('[SP_LOG] Create Subscription: Mutation Error:', error);

      // Get plan name for specific messaging
      const planDetails = allPlansFromApi.find(p => p.paddle_price_id === variables.planId);
      const planName = planDetails?.name || 'selected plan';
      const errorMessage = getErrorMessage(error, `Failed to create subscription to ${planName}.`);
      if (setPersistentMessage) setPersistentMessage({ type: 'error', text: errorMessage });

      setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
      updateProcessingState('creating', false);
    },
  }
  );
  const changePlanMutation = useMutation<ApiResponse<UpdateSubscriptionData>, Error, { subscriptionId: string; newPlanId: string }>(
    {
      mutationFn: (vars) => api.patch<ApiResponse<UpdateSubscriptionData>>(`/subscriptions/${vars.subscriptionId}`, { action: 'change_plan', newPlanId: vars.newPlanId }),
      onMutate: (variables) => {
        // console.log('[SP_LOG] Change Plan: onMutate triggered with variables:', variables);
        const newPlanDetails = allPlansFromApi.find(p => p.paddle_price_id === variables.newPlanId);
        const newPlanName = newPlanDetails?.name || 'selected plan';
        setPersistentMessage(null); // Clear any previous persistent messages
        updateProcessingState('changing', true);
        setPaddleStatus({
          isVisible: true,
          status: 'processing',
          message: `Changing plan to ${newPlanName}...`
        });
      },
      onSuccess: (response, variables) => {
        setIsPlanChangeModalOpen(false);
        const transactionIdFromApi = response.data?.paddle_transaction_id || response.data?.transactionId;

        const newPlanDetails = allPlansFromApi.find(p => p.paddle_price_id === variables.newPlanId);
        const newPlanName = newPlanDetails?.name || 'selected plan';

        const handleImmediateSuccess = async () => {
          toast.success(`Successfully changed to ${newPlanName}!`); // 1. Specific Toast
          // No setPersistentMessage for success
          // No setPaddleStatus to 'success'

          try {
            await handleMutationSuccess(variables.subscriptionId);

            setShowSuccessBanner({ // 2. Specific Banner
              isVisible: true,
              operation: 'change',
              planName: newPlanName
            });
            setTimeout(() => {
              setShowSuccessBanner({ isVisible: false, operation: 'change' });
            }, 8000); // Consistent 8s

          } catch (e) {
            console.error('[SP_LOG] Change Plan (no checkout): Error in handleMutationSuccess:', e);
            toast.error('Plan changed, but an error occurred updating the page. Please refresh.');
          }
          finally {
            updateProcessingState('changing', false);
            setPaddleStatus({ isVisible: false, status: 'loading', message: '' }); // Clear/hide PaddleStatusDisplay
          }
        };

        if (response.success) {
          if (transactionIdFromApi && window.Paddle && typeof window.Paddle.Checkout.open === 'function') {
            // --- ENHANCED LOGGING HERE ---
            // console.log(`[SP_LOG_CHANGE_PLAN_DEBUG] Attempting to set pendingCheckoutAction. Current state:`, JSON.stringify(pendingCheckoutAction));
            // console.log(`[SP_LOG_CHANGE_PLAN_DEBUG] Details for pending action: actionType='changePlan', transactionId='${transactionIdFromApi}', subscriptionIdToUpdate='${variables.subscriptionId}', newPlanName='${newPlanName}'`);

            setPendingCheckoutAction({
              actionType: 'changePlan',
              transactionId: transactionIdFromApi,
              subscriptionIdToUpdate: variables.subscriptionId,
              newPlanName: newPlanName
            });
            // console.log(`[SP_LOG_CHANGE_PLAN_DEBUG] pendingCheckoutAction has been set. New state should reflect the details above.`);
            // --- END OF ENHANCED LOGGING ---

            toast.info(`Finalizing plan change to ${newPlanName}...`);
            setPaddleStatus({
              isVisible: true,
              status: 'processing',
              message: `Opening checkout for ${newPlanName}...`
            });

            console.error('[[SP_LOG_CRITICAL_CHANGE_PLAN]] Attempting to open Paddle Checkout for Change Plan. Transaction ID:', transactionIdFromApi, 'New Plan ID:', variables.newPlanId);

            window.Paddle.Checkout.open({
              transactionId: transactionIdFromApi,
              eventCallback: function (eventData: PaddleEventData) {
                // YOUR EXISTING COMPREHENSIVE eventCallback (lines ~632-690) REMAINS UNCHANGED
                // ... existing code ...
                // console.error('[[SP_LOG_CRITICAL_CHANGE_PLAN]] LOCAL Change Plan eventCallback INVOKED. Event Name:', eventData.name, 'Raw Event Data:', eventData); // Critical but perhaps too noisy for general dev
                const checkoutId = eventData.data?.checkout?.id;
                const currentPlanNameForCallback = newPlanName;

                if (eventData.name === 'checkout.completed') {
                  console.log('[[SP_LOG_CHANGE_PLAN]] LOCAL Change Plan: checkout.completed DETECTED.'); // Changed from error to log
                  // alert('[[DEBUG_CHANGE_PLAN]] LOCAL Change Plan: checkout.completed DETECTED! Processing...');

                  if (checkoutId && lastProcessedCheckoutId === checkoutId) {
                    // console.warn('[SP_LOG_CHANGE_PLAN] Local: Already processed checkout.completed for ID (within local callback):', checkoutId, '- skipping further local processing.');
                    return;
                  }
                  if (checkoutId) {
                    setLastProcessedCheckoutId(checkoutId);
                    // console.log('[SP_LOG_CHANGE_PLAN] Local: Set lastProcessedCheckoutId to:', checkoutId);
                  }

                  // Perform critical UI updates and data refresh directly
                  // We are not awaiting handleMutationSuccess here directly in the event callback
                  // to see if detaching it from the direct await helps.
                  // We'll call it and let it run.
                  handleMutationSuccess(variables.subscriptionId)
                    .then(() => {
                      // console.log('[SP_LOG_CHANGE_PLAN] Local: handleMutationSuccess (called from checkout.completed) resolved successfully.');
                      setShowSuccessBanner({ isVisible: true, operation: 'change', planName: currentPlanNameForCallback });
                      setTimeout(() => setShowSuccessBanner({ isVisible: false, operation: 'change' }), 8000);
                    })
                    .catch(error => {
                      console.error('[SP_LOG_CHANGE_PLAN] Local: Error from handleMutationSuccess (called from checkout.completed):', error); // Keep error log
                      toast.error(`Plan changed, but an error occurred updating the page. Please refresh. Details: ${error.message}`, { duration: 8000 });
                    })
                    .finally(() => {
                      updateProcessingState('changing', false);
                      // The global handler in Layout.tsx is already closing the checkout.
                      // If we find that the global handler closing it PREVENTS this local one from running,
                      // then we'd need to re-evaluate. For now, assume global close is fine.
                      // If Layout.tsx is NOT closing it, uncomment the block below.
                      /*
                      // console.log('[[SP_LOG_CRITICAL_CHANGE_PLAN]] Local: Attempting to close Paddle checkout (if not already closed by global).');
                      if (window.Paddle && window.Paddle.Checkout && typeof window.Paddle.Checkout.close === 'function') {
                          window.Paddle.Checkout.close();
                          // console.log('[[SP_LOG_CRITICAL_CHANGE_PLAN]] Local: Paddle checkout overlay close() called.');
                      }
                      */
                    });

                  toast.success(`Successfully changed to ${currentPlanNameForCallback}! UI refreshing...`);
                  setPaddleStatus({ isVisible: true, status: 'success', message: `Plan changed to ${currentPlanNameForCallback} successfully!` });
                  setTimeout(() => setPaddleStatus({ isVisible: false, status: 'loading', message: '' }), 3000);

                } else if (eventData.name === 'checkout.loaded') {
                  // console.log('[[SP_LOG_CHANGE_PLAN]] Local callback: checkout.loaded');
                  setPaddleStatus({ isVisible: true, status: 'processing', message: `Please complete payment for ${currentPlanNameForCallback}...` });
                } else if (eventData.name === 'checkout.closed') {
                  // console.log('[[SP_LOG_CHANGE_PLAN]] Local callback: checkout.closed. Was payment completed?', eventData.data?.checkout?.status === 'completed');
                  // Add any necessary cleanup if the checkout is closed before completion by the user
                  if (eventData.data?.checkout?.status !== 'completed') {
                    toast.info(`Plan change to ${currentPlanNameForCallback} was closed before completion.`);
                    updateProcessingState('changing', false);
                    setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
                  }
                } else {
                  // console.log(`[[SP_LOG_CHANGE_PLAN]] Local callback: Unhandled event - ${eventData.name}`);
                }
              }
            });
          } else if (response.message && (response.message.includes("No payment required") || response.message.includes("successfully scheduled"))) {
            handleImmediateSuccess();
          } else {
            // YOUR EXISTING ERROR HANDLING (before checkout open) - KEEP AS IS (lines ~663-689 in your file)
            // This includes the console.error, alert, and fallback to handleSuccessLocal or error toasts.
            // Ensure this part correctly handles scenarios where response.success is true but no transactionId,
            // or when response.success is false.
            console.error('[[SP_LOG_CRITICAL_CHANGE_PLAN]] SKIPPING Paddle.Checkout.open for Change Plan. Condition not met or no transactionId.', { // Keep critical error
              hasTransactionId: !!transactionIdFromApi,
              isPaddleAvailable: !!window.Paddle,
              responseSuccess: response.success,
              responseMessage: response.message,
              responseData: response.data
            });
            // alert('[[DEBUG_CHANGE_PLAN]] SKIPPING Paddle Checkout open for Change Plan. Check console for reason.');
            if (response.success) { // If API call was success but no transaction ID (e.g. free plan change)
              // console.warn('[[SP_LOG_CRITICAL_CHANGE_PLAN]] Change Plan was successful but no transactionId for Paddle. Assuming no payment needed.'); // Keep important warnings
              const handleSuccessLocal = async () => {
                toast.success(`Successfully changed to ${newPlanName}!`);
                setPersistentMessage({ type: 'success', text: `Successfully changed to ${newPlanName}!` });
                setShowSuccessBanner({
                  isVisible: true,
                  operation: 'change',
                  planName: newPlanName
                });

                try {
                  await handleMutationSuccess(variables.subscriptionId);

                  setTimeout(() => {
                    setShowSuccessBanner({ isVisible: false, operation: 'change' });
                  }, 5000);

                } finally {
                  updateProcessingState('changing', false);
                  setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
                }
              };
              handleSuccessLocal();
            } else {
              const errorMsg = response?.message || response?.error || `Failed to change plan to ${newPlanName}.`;
              toast.error(errorMsg);
              setPersistentMessage({ type: 'error', text: errorMsg });
              setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
              updateProcessingState('changing', false);
            }
          }
        } else {
          const errorMsg = response?.message || response?.error || `Failed to change plan to ${newPlanName}.`;
          console.error('[SP_LOG] Change Plan: API call not successful.', response);
          toast.error(errorMsg);
          setPersistentMessage({ type: 'error', text: errorMsg });
          setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
          updateProcessingState('changing', false);
        }
      },
      onError: (err: any, variables) => {
        setIsPlanChangeModalOpen(false);
        console.error('[SP_LOG] Change Plan: Mutation Error. Error:', err, 'Variables:', variables);

        // Get plan name for specific messaging
        const newPlanDetails = allPlansFromApi.find(p => p.paddle_price_id === variables.newPlanId);
        const newPlanName = newPlanDetails?.name || 'selected plan';

        const isTimeoutError = err.code === 'ECONNABORTED' ||
          err.message?.includes('timeout') ||
          err.message?.includes('45000ms exceeded');

        if (isTimeoutError) {
          toast.warning(`Plan change to ${newPlanName} took longer than expected. Checking status...`, {
            duration: 6000
          });
          setPersistentMessage({
            type: 'info',
            text: `Plan change to ${newPlanName} request sent. Please wait while we verify the status...`
          });

          handleTimeoutRecovery('change', variables.newPlanId, variables.subscriptionId);

        } else {
          const errorMessage = getErrorMessage(err, `An unknown error occurred during plan change to ${newPlanName}.`);
          toast.error(`Plan change failed: ${errorMessage}`);
          setPersistentMessage({ type: 'error', text: `Plan change failed: ${errorMessage}` });
        }

        setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
        updateProcessingState('changing', false);
      },
    }
  );
  const cancelSubscriptionMutation = useMutation<ApiResponse<UpdateSubscriptionData>, Error, { subscriptionId: string }>({
    mutationFn: (vars) => {
      // console.log('[SP_LOG] Cancel Subscription: mutationFn called with vars:', vars);
      return api.patch(`/subscriptions/${vars.subscriptionId}`, { action: 'cancel' });
    },
    onMutate: (variables) => {
      // console.log('[SP_LOG] Cancel Subscription: onMutate triggered with variables:', variables);

      // Get current plan name for specific messaging
      const currentPlanName = getCurrentPlanDisplay();

      setPersistentMessage(null); // Clear any previous persistent messages
      updateProcessingState('canceling', true);
      setPaddleStatus({
        isVisible: true,
        status: 'processing',
        message: `Canceling ${currentPlanName} subscription...`
      });
    },
    onSuccess: async (response, variables) => {
      const currentPlanName = getCurrentPlanDisplay();

      if (response.success) {
        const successMsg = response.message || `${currentPlanName} subscription cancelled successfully. It will remain active until the end of the current billing period.`;
        toast.success(successMsg); // 1. Specific Toast
        // No setPersistentMessage for success
        // No setPaddleStatus to 'success'

        try {
          await handleMutationSuccess(variables.subscriptionId);

          setShowSuccessBanner({ // 2. Specific Banner
            isVisible: true,
            operation: 'cancel',
            planName: currentPlanName // Plan name might not be strictly needed for cancel banner, but good for consistency
          });
          setTimeout(() => {
            setShowSuccessBanner({ isVisible: false, operation: 'cancel' });
          }, 8000); // Consistent 8s
        } catch (e) {
          console.error('[SP_LOG] Cancel Subscription: Error in handleMutationSuccess:', e);
          toast.error('Subscription cancelled, but an error occurred updating the page. Please refresh.');
        } finally {
          updateProcessingState('canceling', false);
          setPaddleStatus({ isVisible: false, status: 'loading', message: '' }); // Clear/hide PaddleStatusDisplay
        }
      } else {
        const errorMsg = response.message || response.error || `Failed to cancel ${currentPlanName} subscription.`;
        toast.error(errorMsg);
        if (setPersistentMessage) setPersistentMessage({ type: 'error', text: errorMsg });
        updateProcessingState('canceling', false);
        setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
      }
    },
    onError: (err: any, variables) => {
      console.error('[SP_LOG] Cancel Subscription: onError. Error:', err);
      console.error('[SP_LOG] Cancel Subscription: onError. Variables:', variables);

      const currentPlanName = getCurrentPlanDisplay();
      const isTimeoutError = err.code === 'ECONNABORTED' ||
        err.message?.includes('timeout') ||
        err.message?.includes('45000ms exceeded');

      if (isTimeoutError) {
        toast.warning(`${currentPlanName} cancellation took longer than expected. Checking status...`, {
          duration: 6000
        });
        setPersistentMessage({
          type: 'info',
          text: `${currentPlanName} cancellation request sent. Please wait while we verify the status...`
        });

        handleCancellationTimeoutRecovery(variables.subscriptionId);

      } else {
        const errorMsg = getErrorMessage(err, `An unknown error occurred during cancellation.`);
        toast.error(`Cancellation failed: ${errorMsg}`);
        setPersistentMessage({ type: 'error', text: `Cancellation failed: ${errorMsg}` });
      }

      setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
      updateProcessingState('canceling', false);
    },
  }
  );


  // ✅ 4. Update the PaddleStatusDisplay component to handle the new status values
  const PaddleStatusDisplay: React.FC = () => {
    if (!paddleStatus.isVisible) return null;

    return (
      <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm ${paddleStatus.status === 'success' ? 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:text-green-200 dark:border-green-700' :
        paddleStatus.status === 'error' ? 'bg-red-100 border-red-300 text-red-800 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700' :
          paddleStatus.status === 'closed' ? 'bg-yellow-100 border-yellow-300 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-700' :
            'bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-700'
        } border`}>
        <div className="flex items-center gap-2">
          {(paddleStatus.status === 'loading' || paddleStatus.status === 'processing') && (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          {paddleStatus.status === 'success' && (
            <div className="h-4 w-4 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">✓</div>
          )}
          {paddleStatus.status === 'error' && (
            <div className="h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">✕</div>
          )}
          <span className="text-sm font-medium">{paddleStatus.message}</span>
        </div>
      </div>
    );
  };

  // UpgradeSuccessBanner Component
  const UpgradeSuccessBanner: React.FC = () => {
    if (!showSuccessBanner.isVisible) return null;

    const operationMessages = {
      create: `Successfully subscribed to ${showSuccessBanner.planName || 'your new plan'}!`,
      change: `Successfully changed to ${showSuccessBanner.planName || 'your new plan'}!`,
      cancel: 'Subscription cancelled successfully!',
      reactivate: 'Subscription reactivated successfully!'
    };

    return (
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:text-green-200 dark:border-green-700 border p-4 rounded-lg shadow-lg max-w-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">
              {operationMessages[showSuccessBanner.operation]}
            </span>
          </div>
          <button
            onClick={() => setShowSuccessBanner({ isVisible: false, operation: 'create' })}
            className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
          >
            ✕
          </button>
        </div>
      </div>
    );
  };

  const sortedPlansForModal = useMemo(() => {
    if (!allPlansFromApi || allPlansFromApi.length === 0) return [];

    const getEffectiveMonthlyPrice = (plan: Plan): number => {
      if (plan.price_yearly != null && Number(plan.price_yearly) > 0) {
        // It's a yearly plan, calculate effective monthly price
        return Number(plan.price_yearly) / 12;
      }
      // It's a monthly plan (price_monthly is NOT NULL)
      // or a yearly plan with 0 yearly price (shouldn't happen for paid plans)
      return Number(plan.price_monthly);
    };

    // For secondary sorting if prices are equal
    const getBaseName = (name: string): string => {
      return String(name).replace(' Monthly', '').replace(' Yearly', '');
    };
    const isMonthly = (name: string): boolean => String(name).includes('Monthly');
    const planTypeOrder: Record<string, number> = {
      'Basic': 1,
      'Pro': 2,
      'Enterprise': 3
    };

    return [...allPlansFromApi]
      .filter(plan => plan.name?.toLowerCase() !== 'free' && plan.paddle_price_id) // Filter out free/invalid plans first
      .sort((a, b) => {
        const priceA = getEffectiveMonthlyPrice(a);
        const priceB = getEffectiveMonthlyPrice(b);

        if (priceA !== priceB) {
          return priceA - priceB; // Sort by effective monthly price
        }

        // Secondary sorting: by plan type (Basic, Pro, Enterprise)
        const baseNameA = getBaseName(a.name);
        const baseNameB = getBaseName(b.name);
        const orderA = planTypeOrder[baseNameA] || 99;
        const orderB = planTypeOrder[baseNameB] || 99;

        if (orderA !== orderB) {
          return orderA - orderB;
        }

        // Tertiary sorting: Monthly before Yearly if plan type and price are the same
        if (isMonthly(a.name) && !isMonthly(b.name)) {
          return -1;
        }
        if (!isMonthly(a.name) && isMonthly(b.name)) {
          return 1;
        }

        return a.name.localeCompare(b.name); // Final fallback
      });
  }, [allPlansFromApi]);

  // ✅ MOVED: Define placeholderIdForModal BEFORE using it
  const currentSubscriptionObjectForModal = useMemo(() => {
    if (!subscriptionDataArray || !activeSubscriptionId) {
      return null;
    }
    // Find the subscription object that matches the activeSubscriptionId
    // This object should contain 'plan_placeholder_id'.
    return subscriptionDataArray.find(sub => sub.paddle_subscription_id === activeSubscriptionId);
  }, [subscriptionDataArray, activeSubscriptionId]);

  const placeholderIdForModal = currentSubscriptionObjectForModal?.plan_placeholder_id;

  const billingCycleForModal = useMemo(() => {
    if (!placeholderIdForModal || !allPlansFromApi || allPlansFromApi.length === 0) {
      // Fallback to the component's general billingCycle state if specific plan data isn't available
      return billingCycle || 'monthly';
    }
    const activePlanDefinition = allPlansFromApi.find(
      p => p.paddle_price_id === placeholderIdForModal // p.paddle_price_id is the placeholder
    );
    if (activePlanDefinition) {
      return activePlanDefinition.price_yearly != null ? 'yearly' : 'monthly';
    }
    return billingCycle || 'monthly'; // Fallback
  }, [placeholderIdForModal, allPlansFromApi, billingCycle]); // Added billingCycle as a dependency for fallback

  // Debugging useEffect for modal props
  useEffect(() => {
    if (isPlanChangeModalOpen) {
      // console.log('[SP_LOG] PlanChangeModal about to open with props:', { // Often too verbose
      //   activeUserPaddlePriceId: placeholderIdForModal,
      //   plansLength: allPlansFromApi?.length,
      //   billingCycle: billingCycleForModal,
      // });
    }
  }, [isPlanChangeModalOpen, placeholderIdForModal, allPlansFromApi, billingCycleForModal]);
  // == END OF NEW LOGIC FOR PlanChangeModal PROPS ==


  // == Existing derived states/variables ==
  const showManageExistingPlanSection = currentSubscriptionToDisplay !== undefined;

  const getPlansForCycle = useCallback((cycle: 'monthly' | 'yearly') => {
    if (!sortedPlansForModal || sortedPlansForModal.length === 0) return [];

    return sortedPlansForModal.filter(plan => {
      const isFree = plan.name?.toLowerCase() === 'free';
      if (isFree) return false; // Exclude free plan from paid options

      // Check if the plan matches the desired billing cycle
      if (cycle === 'monthly') {
        // A plan is monthly if it has a monthly price AND does NOT have a yearly price (or yearly price is 0/null)
        return plan.price_monthly != null && Number(plan.price_monthly) > 0 &&
          (plan.price_yearly == null || Number(plan.price_yearly) === 0);
      } else { // cycle === 'yearly'
        // A plan is yearly if it has a yearly price
        return plan.price_yearly != null && Number(plan.price_yearly) > 0;
      }
    });
  }, [sortedPlansForModal]);

  const monthlyPlansToDisplay = useMemo(() => getPlansForCycle('monthly'), [getPlansForCycle]);
  const yearlyPlansToDisplay = useMemo(() => getPlansForCycle('yearly'), [getPlansForCycle]);
  // == Existing handler functions ==
  const handleSubscribe = (paddlePriceId: string) => {
    if (!paddlePriceId) {
      toast.error("Cannot subscribe: Plan is missing a valid payment ID.");
      return;
    }
    // console.log(`[SP_LOG] handleSubscribe called with paddlePriceId: ${paddlePriceId}, promoCode: ${promoCode}`);
    setPersistentMessage(null);
    createSubscriptionMutation.mutate({ planId: paddlePriceId, promoCode: promoCode || undefined });
  };

  const handleOpenPlanChange = () => {
    // console.log('[SP_LOG] handleOpenPlanChange called. Current activeSubscriptionId:', activeSubscriptionId);
    setPersistentMessage(null);
    setIsPlanChangeModalOpen(true);
    // console.log('[SP_LOG] isPlanChangeModalOpen set to true');
  };

  // Ensure handleConfirmPlanChange expects a placeholder_id
  const handleConfirmPlanChange = (newPlanPlaceholderId: string) => {
    // console.log(`[SP_LOG] handleConfirmPlanChange called with newPlanPlaceholderId: ${newPlanPlaceholderId}`);
    if (!newPlanPlaceholderId) {
      toast.error("Cannot change plan: Selected plan is missing a valid payment ID.");
      setIsPlanChangeModalOpen(false);
      return;
    }
    if (activeSubscriptionId) {
      setPersistentMessage(null);
      changePlanMutation.mutate({ subscriptionId: activeSubscriptionId, newPlanId: newPlanPlaceholderId });
    } else {
      toast.error("Cannot change plan: No active subscription identified.");
      setIsPlanChangeModalOpen(false);
    }
  };

  const handleCancelSubscription = () => {
    // console.log('[SP_LOG] handleCancelSubscription called. Current activeSubscriptionId:', activeSubscriptionId);
    const currentPlanName = getCurrentPlanDisplay();
    if (activeSubscriptionId && window.confirm(`Are you sure you want to cancel your ${currentPlanName} subscription? It will remain active until the end of the current billing period.`)) {
      // console.log(`[SP_LOG] handleCancelSubscription: Confirmed. Mutating cancelSubscription for ID: ${activeSubscriptionId}`);
      setPersistentMessage(null);
      cancelSubscriptionMutation.mutate({ subscriptionId: activeSubscriptionId });
    } else {
      // console.log('[SP_LOG] handleCancelSubscription: Not confirmed or no activeSubscriptionId.');
    }
  };

  const handleMutationSuccess = useCallback(async (subscriptionIdToActioned?: string) => {
    // No longer capture user?.uid here, rely on it being fresh in query invalidation context
    // console.log(`[SP_LOG] handleMutationSuccess: Initiated. Actioned subscription ID (if applicable): ${subscriptionIdToActioned}`);
    try {
      // console.log(`[SP_LOG] handleMutationSuccess: Calling refreshUser().`);
      await refreshUser();
      // console.log(`[SP_LOG] handleMutationSuccess: refreshUser() completed.`);
      // console.log(`[SP_LOG] handleMutationSuccess: Invalidating userSubscriptionsList for user: ${user?.uid}.`);
      await tanstackQueryClient.invalidateQueries({
        queryKey: ['userSubscriptionsList', user?.uid]
      });
      if (subscriptionIdToActioned) {
        // console.log(`[SP_LOG] handleMutationSuccess: Invalidating userSubscriptionDetail for actioned sub ID: ${subscriptionIdToActioned} and user: ${user?.uid}.`);
        await tanstackQueryClient.invalidateQueries({
          queryKey: ['userSubscriptionDetail', user?.uid, subscriptionIdToActioned]
        });
      }
      if (activeSubscriptionId && activeSubscriptionId !== subscriptionIdToActioned) {
        // console.log(`[SP_LOG] handleMutationSuccess: Invalidating current activeSubscriptionDetail for ID: ${activeSubscriptionId} and user: ${user?.uid}.`);
        await tanstackQueryClient.invalidateQueries({
          queryKey: ['userSubscriptionDetail', user?.uid, activeSubscriptionId]
        });
      }
      // console.log(`[SP_LOG] handleMutationSuccess: Query invalidations complete.`);
      if (window.dispatchSubscriptionOperationCompleted) {
        // console.log('[SP_LOG] handleMutationSuccess: Dispatching subscription-operation-completed event.');
        window.dispatchSubscriptionOperationCompleted();
      }
      // console.log('[SP_LOG] handleMutationSuccess: Completed successfully.');
    } catch (error) {
      console.error('[SP_LOG] Error in handleMutationSuccess:', error); // Keep error log
      toast.error('An error occurred while refreshing your subscription details.');
    }
  }, [tanstackQueryClient, refreshUser, activeSubscriptionId, user?.uid]);

  useEffect(() => {
    const handleCustomCheckoutCompleted = (event: Event) => {
      const customEvent = event as CustomEvent<{ transactionId: string; paddleEventData: any }>;
      if (!customEvent.detail) {
        console.warn('[SP_LOG_CUSTOM_EVENT_HANDLER] Received "paddleCheckoutActionCompleted" but event.detail was null.'); // Keep warning
        return;
      }
      const { transactionId: completedTransactionId, paddleEventData } = customEvent.detail;

      // console.log('[SP_LOG_CUSTOM_EVENT_HANDLER] Received "paddleCheckoutActionCompleted". Pending action:', JSON.stringify(pendingCheckoutAction), 'Event transaction_id:', completedTransactionId);

      if (pendingCheckoutAction.actionType === 'changePlan' &&
        pendingCheckoutAction.transactionId &&
        pendingCheckoutAction.transactionId === completedTransactionId) {

        // console.log(`[SP_LOG_CUSTOM_EVENT_HANDLER] Matched 'changePlan' action for transaction ${completedTransactionId}. Calling handleMutationSuccess for subscriptionId: ${pendingCheckoutAction.subscriptionIdToUpdate}.`);

        const planNameForToast = pendingCheckoutAction.newPlanName || 'your plan';
        toast.success(`Payment for ${planNameForToast} successful! ...`);
        setPaddleStatus({ isVisible: true, status: 'success', message: `Payment completed for ${planNameForToast}!` });

        if (pendingCheckoutAction.subscriptionIdToUpdate) {
          handleMutationSuccess(pendingCheckoutAction.subscriptionIdToUpdate)
            .then(() => {
              // console.log(`[SP_LOG_CUSTOM_EVENT_HANDLER] handleMutationSuccess completed for ${pendingCheckoutAction.actionType}.`);
              if (pendingCheckoutAction.newPlanName) {
                setShowSuccessBanner({ isVisible: true, operation: 'change', planName: pendingCheckoutAction.newPlanName });
                setTimeout(() => setShowSuccessBanner({ isVisible: false, operation: 'change' }), 8000);
              }
            })
            .catch(err => {
              console.error(`[SP_LOG_CUSTOM_EVENT_HANDLER] Error in handleMutationSuccess after custom event for ${pendingCheckoutAction.actionType}:`, err); // Keep error
              toast.error('An error occurred while finalizing the UI update. Please refresh.');
            })
            .finally(() => {
              updateProcessingState('changing', false);
              setPendingCheckoutAction({ actionType: null, transactionId: null, subscriptionIdToUpdate: null, newPlanName: undefined, newSubscriptionPaddleId: null });
              setTimeout(() => setPaddleStatus({ isVisible: false, status: 'loading', message: '' }), 3000);
            });
        } else {
          console.error('[SP_LOG_CUSTOM_EVENT_HANDLER] subscriptionIdToUpdate is null for changePlan. Cannot call handleMutationSuccess.'); // Keep error
          toast.error('Internal error: Missing subscription ID for UI update after plan change. Please refresh.');
          updateProcessingState('changing', false);
          setPendingCheckoutAction({ actionType: null, transactionId: null, subscriptionIdToUpdate: null, newPlanName: undefined, newSubscriptionPaddleId: null });
          setTimeout(() => setPaddleStatus({ isVisible: false, status: 'loading', message: '' }), 3000);
        }
        // --- START OF NEW ELSE IF BLOCK FOR CREATE SUBSCRIPTION ---
      } else if (pendingCheckoutAction.actionType === 'createSubscription' &&
        pendingCheckoutAction.transactionId &&
        pendingCheckoutAction.transactionId === completedTransactionId) {

        // console.log(`[SP_LOG_CUSTOM_EVENT_HANDLER] Matched 'createSubscription' action for transaction ${completedTransactionId}.`);

        const planNameForToast = pendingCheckoutAction.newPlanName || 'selected plan';
        toast.success(`Successfully subscribed to ${planNameForToast}! ...`)
        setPaddleStatus({ isVisible: true, status: 'success', message: `Payment completed for ${planNameForToast}!` });

        // Use the newSubscriptionPaddleId stored in pendingCheckoutAction
        // Attempt to get subscription_id from the paddleEventData if not already set from initial API call
        let subscriptionIdForRefresh = pendingCheckoutAction.newSubscriptionPaddleId;
        if (!subscriptionIdForRefresh && paddleEventData) {
          // Check common locations for a subscription ID within Paddle's checkout.completed event data
          const idFromEvent = (paddleEventData as any).subscription_id ||
            (paddleEventData as any).subscription?.id ||
            (paddleEventData as any).checkout?.subscription_id;
          if (idFromEvent) {
            // console.log(`[SP_LOG_CUSTOM_EVENT_HANDLER] 'createSubscription': Found subscription_id ('${idFromEvent}') in paddleEventData from checkout.completed.`);
            subscriptionIdForRefresh = idFromEvent;
          }
        }


        if (subscriptionIdForRefresh) {
          handleMutationSuccess(subscriptionIdForRefresh)
            .then(() => {
              // console.log(`[SP_LOG_CUSTOM_EVENT_HANDLER] handleMutationSuccess completed for 'createSubscription'. New Sub ID: ${subscriptionIdForRefresh}`);
              if (pendingCheckoutAction.newPlanName) {
                setShowSuccessBanner({ isVisible: true, operation: 'create', planName: pendingCheckoutAction.newPlanName });
                setTimeout(() => setShowSuccessBanner({ isVisible: false, operation: 'create' }), 8000);
              }
            })
            .catch(err => {
              console.error(`[SP_LOG_CUSTOM_EVENT_HANDLER] Error in handleMutationSuccess after custom event for 'createSubscription':`, err); // Keep error
              toast.error('An error occurred while finalizing the new subscription. Please refresh.');
            })
            .finally(() => {
              updateProcessingState('creating', false);
              setPendingCheckoutAction({ actionType: null, transactionId: null, subscriptionIdToUpdate: null, newPlanName: undefined, newSubscriptionPaddleId: null });
              setTimeout(() => setPaddleStatus({ isVisible: false, status: 'loading', message: '' }), 3000);
            });
        } else {
          console.warn('[SP_LOG_CUSTOM_EVENT_HANDLER] newSubscriptionPaddleId is null for createSubscription and not found in paddleEventData. Calling handleMutationSuccess without ID (general refresh).'); // Keep warning
          // Fallback to general refresh if specific ID is missing
          handleMutationSuccess()
            .then(() => {
              if (pendingCheckoutAction.newPlanName) {
                setShowSuccessBanner({ isVisible: true, operation: 'create', planName: pendingCheckoutAction.newPlanName });
                setTimeout(() => setShowSuccessBanner({ isVisible: false, operation: 'create' }), 8000);
              }
            })
            .catch(err => console.error(`[SP_LOG_CUSTOM_EVENT_HANDLER] Error in fallback handleMutationSuccess for 'createSubscription':`, err)) // Keep error
            .finally(() => {
              updateProcessingState('creating', false);
              setPendingCheckoutAction({ actionType: null, transactionId: null, subscriptionIdToUpdate: null, newPlanName: undefined, newSubscriptionPaddleId: null });
              setTimeout(() => setPaddleStatus({ isVisible: false, status: 'loading', message: '' }), 3000);
            });
          // REMOVED THE TOAST.ERROR FOR MISSING SUBSCRIPTION ID
        }
        // --- END OF NEW ELSE IF BLOCK ---
      } else {
        // Existing logging for no match or other action types
        if (pendingCheckoutAction.transactionId && (pendingCheckoutAction.actionType === 'changePlan' || pendingCheckoutAction.actionType === 'createSubscription')) {
          // console.log(`[SP_LOG_CUSTOM_EVENT_HANDLER] Received "paddleCheckoutActionCompleted", but it did not match pending "${pendingCheckoutAction.actionType}" action. Expected TxID:`, pendingCheckoutAction.transactionId, "Got TxID:", completedTransactionId);
        } else if (pendingCheckoutAction.transactionId) {
          // console.log('[SP_LOG_CUSTOM_EVENT_HANDLER] Received "paddleCheckoutActionCompleted", but pending action was not "changePlan" or "createSubscription", or no pending action. Current Pending:', JSON.stringify(pendingCheckoutAction));
        }
      }
    };

    window.addEventListener('paddleCheckoutActionCompleted', handleCustomCheckoutCompleted);
    return () => {
      window.removeEventListener('paddleCheckoutActionCompleted', handleCustomCheckoutCompleted);
    };
  }, [pendingCheckoutAction, handleMutationSuccess, updateProcessingState, setPaddleStatus, setShowSuccessBanner]); // Dependencies remain the same

  const handleManualRefresh = useCallback(async () => {
    // console.log('[SP_LOG] Manual Refresh: Initiating full data refresh.');
    setIsRefreshingData(true);
    setPersistentMessage({ type: 'info', text: 'Refreshing subscription data...' });

    try {
      await refreshUser(); // Refresh auth context first
      // console.log('[SP_LOG] Manual Refresh: User profile refetched during manual refresh.');

      // console.log('[SP_LOG] Manual Refresh: Invalidating queries for manual refresh.');
      // Similar to handleMutationSuccess, rely on fresh user.uid from useAuth()
      await tanstackQueryClient.invalidateQueries({ queryKey: ['userSubscriptionsList', user?.uid] });
      if (activeSubscriptionId) {
        await tanstackQueryClient.invalidateQueries({ queryKey: ['userSubscriptionDetail', user?.uid, activeSubscriptionId] });
      }
      await tanstackQueryClient.invalidateQueries({ queryKey: ['subscriptionPlans'] }); // Plans usually don't depend on UID

      toast.success('Subscription data refreshed!');
      setPersistentMessage(null);
    } catch (error) {
      console.error('[SP_LOG] Manual Refresh: Error during refresh:', error); // Keep error
      const errorMessage = getErrorMessage(error, 'Failed to refresh data.');
      toast.error(errorMessage);
      setPersistentMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsRefreshingData(false);
    }
  }, [refreshUser, tanstackQueryClient, activeSubscriptionId, user?.uid]); // Add user?.uid to dependencies

  // ✅ NEW: Better timeout recovery using TanStack Query
  const handleTimeoutRecovery = useCallback(async (
    operationType: 'create' | 'change',
    expectedPlanId: string,
    subscriptionId?: string
  ) => {
    // console.log(`[SP_LOG] Starting timeout recovery for ${operationType} operation`);

    try {
      // ✅ SIMPLE: Force immediate refetch using refetchQueries
      await tanstackQueryClient.refetchQueries({
        queryKey: ['userSubscriptionsList', user?.uid]
      });

      if (subscriptionId) {
        await tanstackQueryClient.refetchQueries({
          queryKey: ['userSubscriptionDetail', user?.uid, subscriptionId]
        });
      }

      // ✅ SIMPLE: Get fresh data immediately after refetch
      const currentData = tanstackQueryClient.getQueryData(['userSubscriptionsList', user?.uid]) as Subscription[] | undefined;

      if (currentData) {
        let isSuccessful = false;

        if (operationType === 'create') {
          isSuccessful = currentData.some(sub => sub.plan_placeholder_id === expectedPlanId);
        } else { // operationType === 'change'
          const targetSub = currentData.find(sub => sub.paddle_subscription_id === subscriptionId);
          isSuccessful = targetSub?.plan_placeholder_id === expectedPlanId;
        }

        if (isSuccessful) {
          // console.log(`[SP_LOG] ${operationType} operation detected as successful after refetch!`);
          toast.success(`${operationType === 'create' ? 'Subscription created' : 'Plan changed'} successfully!`);
          setPersistentMessage({
            type: 'success',
            text: `${operationType === 'create' ? 'Subscription created' : 'Plan changed'} successfully!`
          });

          // ✅ SIMPLE: Call handleMutationSuccess to ensure all data is properly refreshed
          await handleMutationSuccess(subscriptionId || expectedPlanId);

          setShowSuccessBanner({
            isVisible: true,
            operation: operationType,
            planName: expectedPlanId
          });

          setTimeout(() => {
            setShowSuccessBanner({ isVisible: false, operation: operationType });
          }, 5000);

        } else {
          // console.log(`[SP_LOG] ${operationType} operation not yet reflected in data after refetch`);
          toast.info('Please refresh the page to see the latest subscription status.');
          setPersistentMessage({
            type: 'warning',
            text: 'Operation status unclear. Please refresh the page to see the latest status.'
          });
        }
      }

    } catch (error) {
      console.error(`[SP_LOG] Error during timeout recovery:`, error); // Keep error
      toast.error('Failed to verify operation status. Please refresh the page.');
    } finally {
      // ✅ SIMPLE: Always reset processing state
      updateProcessingState(operationType === 'create' ? 'creating' : 'changing', false);
    }

  }, [tanstackQueryClient, user?.uid, handleMutationSuccess, updateProcessingState]);

  // ✅ NEW: Cancellation-specific timeout recovery
  const handleCancellationTimeoutRecovery = useCallback(async (subscriptionId: string) => {
    // console.log(`[SP_LOG] Starting cancellation timeout recovery for subscription: ${subscriptionId}`);

    try {
      // ✅ SIMPLE: Force immediate refetch
      await tanstackQueryClient.refetchQueries({
        queryKey: ['userSubscriptionsList', user?.uid]
      });

      await tanstackQueryClient.refetchQueries({
        queryKey: ['userSubscriptionDetail', user?.uid, subscriptionId]
      });

      // ✅ SIMPLE: Check current data for cancellation
      const currentData = tanstackQueryClient.getQueryData(['userSubscriptionsList', user?.uid]) as Subscription[] | undefined;

      if (currentData) {
        const targetSub = currentData.find(sub => sub.paddle_subscription_id === subscriptionId);

        if (targetSub?.cancel_at_period_end) {
          // console.log(`[SP_LOG] Cancellation operation detected as successful!`);
          toast.success('Subscription cancellation completed successfully!');
          setPersistentMessage({
            type: 'success',
            text: 'Subscription cancelled successfully!'
          });

          await handleMutationSuccess(subscriptionId);

          setShowSuccessBanner({
            isVisible: true,
            operation: 'cancel',
            planName: getCurrentPlanDisplay()
          });

          setTimeout(() => {
            setShowSuccessBanner({ isVisible: false, operation: 'cancel' });
          }, 5000);

        } else {
          toast.info('Please refresh the page to see the latest cancellation status.');
          setPersistentMessage({
            type: 'warning',
            text: 'Cancellation status unclear. Please refresh the page.'
          });
        }
      }

    } catch (error) {
      console.error(`[SP_LOG] Error during cancellation timeout recovery:`, error); // Keep error
      toast.error('Failed to verify cancellation status. Please refresh the page.');
    } finally {
      // ✅ SIMPLE: Always reset processing state
      updateProcessingState('canceling', false);
    }

  }, [tanstackQueryClient, user?.uid, handleMutationSuccess, updateProcessingState, getCurrentPlanDisplay]);

  // FIND the code that defines isLoadingCriticalData - IMPORTANT: it must be defined BEFORE being used
  // If it's missing completely, add it here:
  const isLoadingCriticalData =
    isLoadingSubscriptionsList ||
    isLoadingPlans ||
    (!!activeSubscriptionId && isLoadingDetailedSubscription) ||
    isAuthLoading ||
    !user;

  useEffect(() => {
    if (planToSubscribe && plansArray && !isLoadingCriticalData) {
      const plan = plansArray.find(p => p.paddle_price_id === planToSubscribe);
      if (plan) {
        handleSubscribe(plan.paddle_price_id);
        navigate(location.pathname, { replace: true });
      }
    }
  }, [planToSubscribe, plansArray, isLoadingCriticalData]);

  // Then ADD logging AFTER the isLoadingCriticalData is defined:
  useEffect(() => {
    if (DEBUG && (isLoadingCriticalData || isRefreshingData || isProcessing)) {
      // console.log('[SP_LOG] LOADING STATE DETECTED:', { // Very verbose, comment out for now
      //   isLoadingCriticalData,
      //   isLoadingSubscriptionsList,
      //   isLoadingPlans,
      //   activeSubscriptionId,
      //   isLoadingDetailedSubscription,
      //   isAuthLoading,
      //   userExists: !!user,
      //   currentSubscriptionToDisplayExists: !!currentSubscriptionToDisplay,
      //   isFetchingDetailedSubscription,
      //   isRefreshingData,
      //   isProcessing,
      // });
    }
  }, [
    isLoadingCriticalData,
    isRefreshingData,
    isProcessing,
    isLoadingSubscriptionsList,
    isLoadingPlans,
    activeSubscriptionId,
    isLoadingDetailedSubscription,
    isAuthLoading,
    user,
    currentSubscriptionToDisplay,
    isFetchingDetailedSubscription,
  ]);

  const queryError = subscriptionsListErrorObj || plansErrorObj || (activeSubscriptionId && detailedSubscriptionErrorObj);

  const hasActiveOrPendingSub = useMemo(() => {
    // Check user subscription_status first
    if (user?.subscription_status === 'active') {
      // console.log('[SP_LOG] Active subscription detected from user object');
      return true;
    }

    // ✅ FIXED: Use subscriptionDataArray (not subscriptionsList)
    if (subscriptionDataArray && subscriptionDataArray.length > 0) {
      const hasActive = subscriptionDataArray.some(sub =>
        [SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIALING, SubscriptionStatus.PAST_DUE].includes(sub.status)
      );
      // console.log('[SP_LOG] Active subscription from list:', hasActive);
      return hasActive;
    }

    // console.log('[SP_LOG] No active subscription detected');
    return false;
  }, [user?.subscription_status, subscriptionDataArray]);

  // == Conditional returns for loading/error/no subs states ==
  if (isLoadingCriticalData || isRefreshingData) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-1/4" />
        <div className="flex justify-start my-4">
          <Skeleton className="h-10 w-20 mr-1 rounded-md" />
          <Skeleton className="h-10 w-20 rounded-md" />
        </div>
        <Skeleton className="h-32 w-full max-w-lg" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-48 w-full" />)}
        </div>
      </div>
    );
  }
  if (queryError && !persistentMessage) {
    const displayErrorMessage = getErrorMessage(queryError, 'An error occurred while loading subscription data or plans. Please try again later.');
    return <div className="text-destructive p-4">{displayErrorMessage}</div>;
  }
  if (!isLoadingCriticalData && !queryError && !hasActiveOrPendingSub && subscriptionDataArray?.length === 0) {
    return (
      <div className="p-8 text-left">
        <div className="max-w-6xl mx-auto">
          {/* NEW SUBSCRIBER BONUS BANNER - ONLY ADDITION */}
          {isEligibleForNewUserBonusMessage && (
            <div className="my-6 p-4 bg-blue-100 border border-blue-300 rounded-lg text-center text-blue-800 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-200 shadow">
              <h3 className="text-xl font-semibold mb-2">🎉 Welcome Bonus! 🎉</h3>
              <p className="text-sm md:text-base">
                Subscribe to your <strong>first paid plan</strong> and receive a special one-time credit bonus!
                <br className="hidden sm:inline" /> The higher the plan, the bigger your starting boost. This offer is available once per account.
              </p>
            </div>
          )}

          <Tabs value={billingCycle} onValueChange={(value) => setBillingCycle(value as 'monthly' | 'yearly')} className="w-full">
            <div className="flex justify-center mb-4">
              <TabsList className="grid w-full grid-cols-2 md:w-[240px]">
                <TabsTrigger value="monthly" className="text-sm">Monthly</TabsTrigger>
                <TabsTrigger value="yearly" className="text-sm">Yearly</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="monthly">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {monthlyPlansToDisplay.map((plan) => {
                  return (
                    <Card key={plan.id + '-monthly-new'} className="flex flex-col bg-card shadow-lg rounded-xl overflow-hidden">
                      <CardHeader className="p-6">
                        <CardTitle className="text-xl lg:text-2xl font-bold text-card-foreground">{plan.name}</CardTitle>
                        {plan.description && (
                          <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                            {plan.description}
                          </CardDescription>
                        )}
                      </CardHeader>
                      <CardContent className="flex-grow px-6 space-y-4">
                        <div>
                          <span className="text-4xl font-extrabold text-card-foreground">${plan.price_monthly}</span>
                          <span className="text-sm font-medium text-muted-foreground"> / month</span>
                        </div>
                        <div className="mt-4 mb-4">
                          <Button
                            onClick={() => handleSubscribe(plan.paddle_price_id || '')}
                            disabled={isProcessing || !plan.paddle_price_id || !plan.price_monthly || isAuthLoading || !user}
                            className="w-full py-3 text-base font-semibold"
                            size="lg"
                          >
                            {isProcessing || isAuthLoading || !user ?
                              <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Processing...</> :
                              'Try Monthly'}
                          </Button>
                        </div>
                        <PlanFeaturesDisplay
                          features={plan.features}
                          showActivationPerks={true}
                          highlightAsNewUserBonus={false} // ADD THIS PROP - false for existing users
                          className="mt-4"
                          billingTerm="monthly"
                        />
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="yearly">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {yearlyPlansToDisplay.map((plan) => {
                  const baseName = plan.name.replace(' Yearly', '');
                  const monthlyCounterpart = allPlansFromApi.find(p => p.name === `${baseName} Monthly`);
                  let savingsPercentage: number | null = null;
                  let effectiveMonthlyPrice: string | null = null;
                  const descriptionToDisplay = plan.description || monthlyCounterpart?.description;
                  const featuresToDisplay = plan.features || monthlyCounterpart?.features;

                  if (monthlyCounterpart && monthlyCounterpart.price_monthly && plan.price_yearly) {
                    const twelveMonthCost = Number(monthlyCounterpart.price_monthly) * 12;
                    const yearlyPlanCost = Number(plan.price_yearly);
                    if (twelveMonthCost > 0 && yearlyPlanCost > 0 && twelveMonthCost > yearlyPlanCost) {
                      savingsPercentage = Math.round(((twelveMonthCost - yearlyPlanCost) / twelveMonthCost) * 100);
                      effectiveMonthlyPrice = (yearlyPlanCost / 12).toFixed(2);
                    }
                  }

                  return (
                    <Card key={plan.id + '-yearly-new'} className="flex flex-col bg-card shadow-lg rounded-xl overflow-hidden">
                      <CardHeader className="p-6">
                        <CardTitle className="text-xl lg:text-2xl font-bold text-card-foreground">{plan.name}</CardTitle>
                        {descriptionToDisplay && (
                          <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                            {descriptionToDisplay}
                          </CardDescription>
                        )}

                      </CardHeader>
                      {savingsPercentage !== null && (
                        <span className="block text-xs text-green-600 dark:text-green-400 mt-2 font-semibold bg-green-100 dark:bg-green-900 px-2 py-1 rounded-md inline-block">
                          Save {savingsPercentage}% vs Monthly
                        </span>
                      )}
                      <CardContent className="flex-grow p-6 space-y-4">
                        <div>
                          <span className="text-4xl font-extrabold text-card-foreground">${plan.price_yearly}</span>
                          <span className="text-sm font-medium text-muted-foreground"> / year</span>
                          {effectiveMonthlyPrice !== null && (
                            <p className="text-xs text-muted-foreground mt-1">(Effectively ${effectiveMonthlyPrice}/month)</p>
                          )}
                          <Button
                            onClick={() => handleSubscribe(plan.paddle_price_id || '')}
                            disabled={isProcessing || !plan.paddle_price_id || !plan.price_yearly || isAuthLoading || !user}
                            className="w-full py-3 mt-4 text-base font-semibold"
                            size="lg"
                          >
                            {isProcessing || isAuthLoading || !user ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Processing...</>
                              : 'Try Yearly'}
                          </Button>
                        </div>
                        <PlanFeaturesDisplay
                          features={featuresToDisplay}
                          showActivationPerks={true}
                          highlightAsNewUserBonus={false} // ADD THIS PROP - false for existing users
                          showMonthlyEquivalent={true}
                          className="mt-4 pt-4"
                        />
                      </CardContent>
                      <CardFooter className="p-6 bg-muted/30 dark:bg-muted/10 mt-auto">

                      </CardFooter>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    );
  }

  const currentPlanDetails = allPlansFromApi.find(p => p.id === currentSubscriptionToDisplay?.plan_id);

  // == Main JSX Return for SubscriptionsContent ==
  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-8">
      {/* Add the new UI components */}
      <PaddleStatusDisplay />
      <UpgradeSuccessBanner />

      {/* Container for Inline Paddle Checkout - REMOVED */}
      {/* {isInlineCheckoutActive && (
        <div id="paddle-checkout-container" className="mb-6 rounded-lg shadow-md overflow-hidden">
          {/* Paddle.js will mount the inline checkout iframe here * /}
        </div>
      )} */}

      <h1 className="text-3xl font-bold">Manage Subscription</h1>

      {persistentMessage && (
        <div className={`p-3 rounded-md text-sm border ${persistentMessage.type === 'success' ? 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:text-green-200 dark:border-green-700' :
          persistentMessage.type === 'error' ? 'bg-red-100 border-red-300 text-red-800 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700' :
            'bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-700'
          }`}>
          {persistentMessage.text}
        </div>
      )}

      {showManageExistingPlanSection ? (
        <div className="space-y-6">
          <Card className="max-w-lg">
            <>
              <CardHeader>
                <CardTitle>Your Current Plan {isFetchingDetailedSubscription && activeSubscriptionId && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}</CardTitle>                <CardDescription>
                  {(isFetchingDetailedSubscription && activeSubscriptionId && !currentPlanDetails) ? <Skeleton className="h-5 w-24 inline-block" /> : (getCurrentPlanDisplay())}
                  - Status:


                  <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${currentSubscriptionToDisplay?.status === SubscriptionStatus.ACTIVE && currentSubscriptionToDisplay?.cancel_at_period_end ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                    currentSubscriptionToDisplay?.status === SubscriptionStatus.ACTIVE ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      currentSubscriptionToDisplay?.status === SubscriptionStatus.TRIALING ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        currentSubscriptionToDisplay?.status === SubscriptionStatus.CANCELED ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}>{currentSubscriptionToDisplay?.status === SubscriptionStatus.ACTIVE && currentSubscriptionToDisplay?.cancel_at_period_end ? 'Canceling' : currentSubscriptionToDisplay?.status}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {(isFetchingDetailedSubscription && activeSubscriptionId) ? (
                  <>
                    <Skeleton className="h-4 w-3/4 mb-1" />
                    <Skeleton className="h-4 w-1/2 mb-1" />
                    <Skeleton className="h-4 w-2/3" />
                  </>
                ) : (
                  <>
                    {(() => {
                      const endsAtDate = currentSubscriptionToDisplay?.ends_at ? new Date(currentSubscriptionToDisplay.ends_at) : null;
                      const trialEndsAtDate = currentSubscriptionToDisplay?.trial_ends_at ? new Date(currentSubscriptionToDisplay.trial_ends_at) : null;
                      const now = new Date();

                      if (trialEndsAtDate && trialEndsAtDate > now) {
                        return <TrialStatus subscription={currentSubscriptionToDisplay} />;
                      } else if (endsAtDate && endsAtDate <= now) {
                        return (
                          <p className="text-orange-600 dark:text-orange-400">
                            Your subscription ended on: {endsAtDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                          </p>
                        );
                      } else if (currentSubscriptionToDisplay?.cancel_at_period_end && (!endsAtDate || endsAtDate > now)) {
                        return (
                          <p className="text-orange-600 dark:text-orange-400">
                            Your subscription is set to cancel at the end of the current period ({currentSubscriptionToDisplay?.current_period_end ? new Date(currentSubscriptionToDisplay.current_period_end).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}).
                          </p>
                        );
                      } else if (currentSubscriptionToDisplay?.status === SubscriptionStatus.ACTIVE) {
                        return (
                          <>
                            {currentSubscriptionToDisplay?.current_period_end && (
                              <p>Current subscription period ends: {new Date(currentSubscriptionToDisplay.current_period_end).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                            )}
                            {currentSubscriptionToDisplay?.next_billing_at && (
                              <p>Next billing date: {new Date(currentSubscriptionToDisplay.next_billing_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                            )}
                          </>
                        );
                      } else if (currentSubscriptionToDisplay?.status === SubscriptionStatus.TRIALING) {
                        // This case handles a trialing subscription that might not have trial_ends_at,
                        // or if TrialStatus component isn't the only thing to show.
                        // If TrialStatus is comprehensive, this block might be redundant or adjusted.
                        // For now, showing current_period_ends if available for a trialing sub.
                        return (
                          <>
                            {currentSubscriptionToDisplay?.current_period_end && (
                              <p>Trial period ends: {new Date(currentSubscriptionToDisplay.current_period_end).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                            )}
                          </>
                        );
                      }
                      return null; // Default: show nothing if no specific state matches
                    })()}
                  </>
                )}
              </CardContent>
              <CardFooter className="flex justify-between items-center">
                {currentSubscriptionToDisplay?.status === SubscriptionStatus.ACTIVE && currentSubscriptionToDisplay?.cancel_at_period_end && (
                  <div className="space-y-4 w-full flex flex-col items-center">
                    {/* Single information box with bullet points */}
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm mb-4">
                      <p>Your subscription is set to cancel at the end of the current period.</p>

                      {/*<li><strong>Change Plan</strong>: Switch to a different plan</li>*/}
                      <p className="mt-2">By choosing <strong>Resume Subcription</strong> you can make your subscription active again</p>

                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
                      {/*<Button 
                        variant="outline" 
                        onClick={handleOpenPlanChange} 
                        disabled={isProcessing || monthlyPlansToDisplay.length <= 1}
                      >
                        Change Plan
                      </Button> */}
                      <Button
                        variant="default"
                        onClick={() => {
                          if (currentSubscriptionToDisplay?.paddle_subscription_id) {
                            const currentPlanName = getCurrentPlanDisplay();
                            setPersistentMessage(null); // Clear any previous persistent messages
                            updateProcessingState('reactivating', true);
                            setPaddleStatus({
                              isVisible: true,
                              status: 'processing',
                              message: `Reactivating ${currentPlanName} subscription...`
                            });

                            api.patch(`/subscriptions/${currentSubscriptionToDisplay.paddle_subscription_id}`, {
                              action: 'reactivate_current_plan'
                            })
                              .then((value: unknown) => {
                                const response = value as ApiResponse<UpdateSubscriptionData>;

                                if (response.success) {
                                  toast.success(response.message || `Your ${currentPlanName} subscription has been successfully reactivated!`); // 1. Specific Toast
                                  // No setPaddleStatus to 'success' here.

                                  handleMutationSuccess(currentSubscriptionToDisplay?.paddle_subscription_id ?? undefined)
                                    .then(() => {
                                      setShowSuccessBanner({ isVisible: true, operation: 'reactivate', planName: currentPlanName }); // 2. Specific Banner
                                      setTimeout(() => setShowSuccessBanner({ isVisible: false, operation: 'reactivate' }), 8000); // Consistent 8s
                                    })
                                    .catch(err => {
                                      console.error('[SP_LOG] Reactivate Plan: Error during post-reactivation data refresh (handleMutationSuccess):', err);
                                      toast.error('Subscription reactivated, but an error occurred updating the page. Please refresh.');
                                      // Persistent message for error if needed
                                      if (setPersistentMessage) setPersistentMessage({ type: 'error', text: 'Subscription reactivated, but an error occurred updating the page. Please refresh.' });
                                    })
                                    .finally(() => {
                                      updateProcessingState('reactivating', false);
                                      setPaddleStatus({ isVisible: false, status: 'loading', message: '' }); // Clear/hide PaddleStatusDisplay
                                    });
                                } else {
                                  const errorMsg = response.message || `Failed to reactivate ${currentPlanName} subscription.`;
                                  toast.error(errorMsg);
                                  if (setPersistentMessage) setPersistentMessage({ type: 'error', text: errorMsg });
                                  setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
                                  updateProcessingState('reactivating', false);
                                }
                              })
                              .catch(error => {
                                const errorMsg = getErrorMessage(error, `Failed to reactivate ${currentPlanName} subscription`);
                                toast.error(errorMsg);
                                if (setPersistentMessage) setPersistentMessage({ type: 'error', text: errorMsg });
                                setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
                                updateProcessingState('reactivating', false);
                              });
                          } else {
                            const errorMsg = "Cannot reactivate plan: Current subscription ID is missing.";
                            toast.error(errorMsg);
                            if (setPersistentMessage) setPersistentMessage({ type: 'error', text: errorMsg });
                            console.error("[SP_LOG] Error reactivating: currentSubscriptionToDisplay or its paddle_subscription_id is missing.", currentSubscriptionToDisplay);
                            updateProcessingState('reactivating', false);
                            setPaddleStatus({ isVisible: false, status: 'loading', message: '' });
                          }
                        }}
                        disabled={processingStates.reactivating}
                      >
                        {processingStates.reactivating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processing...</> : `Resume Subscription`}
                      </Button>
                    </div>
                  </div>
                )}
                {(currentSubscriptionToDisplay?.status === SubscriptionStatus.ACTIVE || currentSubscriptionToDisplay?.status === SubscriptionStatus.TRIALING) && !currentSubscriptionToDisplay?.cancel_at_period_end && (
                  <>
                    <Button variant="outline" onClick={handleOpenPlanChange} disabled={isProcessing || monthlyPlansToDisplay.length <= 1}>Change Plan</Button>
                    <Button variant="destructive" onClick={handleCancelSubscription} disabled={isProcessing || cancelSubscriptionMutation.isPending}>
                      {cancelSubscriptionMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Cancelling...</> : 'Cancel Subscription'}
                    </Button>
                  </>
                )}
                {currentSubscriptionToDisplay?.status === SubscriptionStatus.CANCELED && (
                  <>
                    <p className="text-sm text-muted-foreground">This subscription is cancelled.</p>
                    <Button variant="default" onClick={handleOpenPlanChange} disabled={isProcessing}>
                      Choose New Plan
                    </Button>
                  </>
                )}
              </CardFooter>
            </>
          </Card>



        </div>
      ) : (
        !isLoadingCriticalData && !queryError && !hasActiveOrPendingSub && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-semibold mb-6 text-center">Choose a Plan</h2>
            <Tabs value={billingCycle} onValueChange={(value) => setBillingCycle(value as 'monthly' | 'yearly')} className="w-full">
              <div className="flex justify-left mb-8">
                <TabsList className="grid w-full grid-cols-2 md:w-[240px]">
                  <TabsTrigger value="monthly" className="text-sm">Monthly</TabsTrigger>
                  <TabsTrigger value="yearly" className="text-sm">Yearly</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="monthly">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {monthlyPlansToDisplay.map((plan) => {
                    return (
                      <Card key={plan.id + '-monthly-new'} className="flex flex-col bg-card shadow-lg rounded-xl overflow-hidden">
                        {/* CardHeader remains the same */}
                        <CardHeader className="p-6">
                          <CardTitle className="text-xl lg:text-2xl font-bold text-card-foreground">{plan.name}</CardTitle>
                          {plan.description && (
                            <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                              {plan.description}
                            </CardDescription>
                          )}
                        </CardHeader>
                        {/* CardContent now starts with price, then button */}
                        <CardContent className="flex-grow p-6 space-y-4">
                          <div>
                            <span className="text-4xl font-extrabold text-card-foreground">${plan.price_monthly}</span>
                            <span className="text-sm font-medium text-muted-foreground"> / month</span>
                          </div>

                          {/* MOVED BUTTON AND CHANGED TEXT */}
                          <div className="mt-4"> {/* Add margin if needed, or adjust parent space-y */}
                            <Button
                              onClick={() => handleSubscribe(plan.paddle_price_id || '')}
                              disabled={isProcessing || !plan.paddle_price_id || !plan.price_monthly || isAuthLoading || !user}
                              className="w-full py-3 text-base font-semibold"
                              size="lg"
                            >
                              {isProcessing || isAuthLoading || !user ?
                                <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Processing...</> :
                                'Try Monthly'}
                            </Button>
                          </div>
                          {/* Separator will be added in the next step if desired */}
                          {/* PlanFeaturesDisplay will be called after the button/separator */}
                          <PlanFeaturesDisplay
                            features={plan.features}
                            showActivationPerks={true}
                            highlightAsNewUserBonus={false} // ADD THIS PROP - false for existing users
                            className="mt-4"
                            billingTerm="monthly"
                          />
                        </CardContent>
                        {/* CardFooter is removed as button is now in CardContent */}
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="yearly">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {yearlyPlansToDisplay.map((plan) => {
                    const baseName = plan.name.replace(' Yearly', '');
                    const monthlyCounterpart = allPlansFromApi.find(p => p.name === `${baseName} Monthly`);
                    let savingsPercentage: number | null = null;
                    let effectiveMonthlyPrice: string | null = null;

                    // Use yearly plan's own description/features, or fallback to monthly counterpart's
                    const descriptionToDisplay = plan.description || monthlyCounterpart?.description;
                    const featuresToDisplay = plan.features || monthlyCounterpart?.features;

                    if (monthlyCounterpart && monthlyCounterpart.price_monthly && plan.price_yearly) {
                      const twelveMonthCost = Number(monthlyCounterpart.price_monthly) * 12;
                      const yearlyPlanCost = Number(plan.price_yearly);
                      if (twelveMonthCost > 0 && yearlyPlanCost > 0 && twelveMonthCost > yearlyPlanCost) {
                        savingsPercentage = Math.round(((twelveMonthCost - yearlyPlanCost) / twelveMonthCost) * 100);
                        effectiveMonthlyPrice = (yearlyPlanCost / 12).toFixed(2);
                      }
                    }

                    return (
                      <Card key={plan.id + '-yearly-new'} className="flex flex-col bg-card shadow-lg rounded-xl overflow-hidden">
                        <CardHeader className="p-6">
                          <CardTitle className="text-xl lg:text-2xl font-bold text-card-foreground">{plan.name}</CardTitle>
                          {descriptionToDisplay && (
                            <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                              {descriptionToDisplay}
                            </CardDescription>
                          )}

                        </CardHeader>
                        {savingsPercentage !== null && (
                          <span className="block text-xs text-green-600 dark:text-green-400 mt-2 font-semibold bg-green-100 dark:bg-green-900 px-2 py-1 rounded-md inline-block">
                            Save {savingsPercentage}% vs Monthly
                          </span>
                        )}
                        <CardContent className="flex-grow p-6 space-y-4">
                          <div>
                            <Button
                              onClick={() => handleSubscribe(plan.paddle_price_id || '')}
                              disabled={isProcessing || !plan.paddle_price_id || !plan.price_yearly || isAuthLoading || !user}
                              className="w-full py-3 text-base font-semibold"
                              size="lg"
                            >
                              {isProcessing || isAuthLoading || !user ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Processing...</>
                                : 'Try Yearly'}
                            </Button>
                            <span className="text-4xl font-extrabold text-card-foreground">${plan.price_yearly}</span>
                            <span className="text-sm font-medium text-muted-foreground"> / year</span>
                            {effectiveMonthlyPrice !== null && (
                              <p className="text-xs text-muted-foreground mt-1">(Effectively ${effectiveMonthlyPrice}/month)</p>
                            )}

                          </div>
                          <PlanFeaturesDisplay
                            features={featuresToDisplay}
                            showActivationPerks={true}
                            highlightAsNewUserBonus={false} // ADD THIS PROP - false for existing users
                            showMonthlyEquivalent={true}
                            className="mt-4 pt-4"
                          />
                        </CardContent>
                        <CardFooter className="p-6 bg-muted/30 dark:bg-muted/10 mt-auto">

                        </CardFooter>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        ))}

      {user?.uid && (
        <Card className="mt-10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Billing History</CardTitle>
                <CardDescription>View your past invoices and transactions.</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)} aria-expanded={showHistory}>
                {showHistory ? (<><ChevronUp className="h-4 w-4 mr-2" />Hide History</>) : (<><ChevronDown className="h-4 w-4 mr-2" />Show History</>)}
              </Button>
            </div>
          </CardHeader>
          {showHistory && (
            <CardContent> {/* Wrap InvoiceHistory in CardContent */}
              <InvoiceHistory userId={user.uid} enabled={!!user?.uid && showHistory} />
            </CardContent>
          )}
        </Card>
      )}

      {/* PlanChangeModal is now correctly placed as a sibling to other main content blocks */}
      {/* MODIFIED PlanChangeModal call */}

      <PlanChangeModal
        isOpen={isPlanChangeModalOpen}
        onClose={() => setIsPlanChangeModalOpen(false)}
        activeUserPaddlePriceId={placeholderIdForModal}
        plans={sortedPlansForModal}
        onConfirmChange={handleConfirmPlanChange}
        isLoading={changePlanMutation.isPending}
        billingCycle={billingCycleForModal}
        currentPlanName={currentPlanDetails?.name || ''}
        subscriptionStatus={currentSubscriptionToDisplay?.status}
        currentPlanIsCanceling={currentSubscriptionToDisplay?.cancel_at_period_end === true}
        modalMode={currentSubscriptionToDisplay?.status === SubscriptionStatus.CANCELED ? 'new_subscription' : 'change_plan'}

      />

      {/*Planchange modal end */}

    </div>
  );
}

